import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { baseUrl } from '../components/BaseUrl';
import { Spinner, Container, Row, Col, Card, Alert, Badge, ProgressBar } from 'react-bootstrap';
import axios from 'axios';
import Vimeo from '@u-wave/react-vimeo';
import Swal from 'sweetalert2';
import '../App.css';
import Topbar from '../components/Topbar';
import { MyProSidebarProvider } from "../components/SidebarContext";

// Constants
const API_ENDPOINTS = {
  GET_VIDEO: `${baseUrl}get_video_by_id.php`,
  CHECK_ENROLLMENT: `${baseUrl}check-enrollment.php`,
  UPDATE_PROGRESS: `${baseUrl}update-course-progress.php`
};

const COURSE_ROUTES = {
  'html 5': '/html-course-outline',
  'javascript': '/javascript-course-outline',
  'javascript for cybersecurity': '/cyber-script-course-outline'
};

// Primary color
const PRIMARY_COLOR = '#145AE6';
const PRIMARY_LIGHT = '#4D83FF';
const PRIMARY_DARK = '#0D47E6';

// Custom hook for API calls
const useApi = () => {
  const [loading, setLoading] = useState(false);

  const makeRequest = useCallback(async (config) => {
    setLoading(true);
    try {
      console.log('API Request:', config);
      const response = await axios({
        ...config,
        signal: AbortSignal.timeout ? AbortSignal.timeout(30000) : undefined
      });
      console.log('API Response:', response);
      return response.data;
    } catch (error) {
      console.error('API Error:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  return { makeRequest, loading };
};

// Full Screen Loader Component
const FullScreenLoader = () => (
  <div className="full-screen-loader" style={{ background: `linear-gradient(135deg, ${PRIMARY_COLOR} 0%, ${PRIMARY_DARK} 100%)` }}>
    <div className="loader-content">
      <div className="loader-animation">
        <div className="loader-circle"></div>
        <div className="loader-circle"></div>
        <div className="loader-circle"></div>
      </div>
      <h3 className="loader-text">Loading Your Learning Journey</h3>
      <p className="loader-subtext">Preparing an amazing experience for you</p>
    </div>
  </div>
);

// Video player component
const VideoPlayer = ({ video, onProgress, onEnd, onError }) => {
  const [playerError, setPlayerError] = useState(null);

  const handleError = useCallback((error) => {
    console.error('Vimeo player error:', error);
    setPlayerError('Failed to load video player');
    onError?.(error);
  }, [onError]);

  const handleLoaded = useCallback(() => {
    setPlayerError(null);
  }, []);

  const handleEnd = useCallback(() => {
    console.log('Video completed - marking as watched');
    onEnd?.();
  }, [onEnd]);

  if (!video?.video_url) {
    return (
      <div className="video-placeholder">
        <div className="placeholder-icon">
          <i className="fas fa-video-slash"></i>
        </div>
        <h4>Video Coming Soon</h4>
        <p>This video is being prepared and will be available shortly</p>
      </div>
    );
  }

  if (playerError) {
    return (
      <div className="video-error">
        <div className="error-icon">
          <i className="fas fa-exclamation-triangle"></i>
        </div>
        <h4>Playback Issue</h4>
        <p>We're having trouble loading this video</p>
        <button 
          className="btn-retry"
          onClick={handleLoaded}
        >
          <i className="fas fa-redo me-2"></i>
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="video-container">
      <Vimeo
        video={video.video_url}
        autoplay={true}
        showTitle={false}
        showPortrait={false}
        responsive={true}
        controls={true}
        showByline={false}
        loop={false}
        playsInline={true}
        onReady={handleLoaded}
        onError={handleError}
        onPlay={() => console.log('Video started playing')}
        onPause={() => console.log('Video paused')}
        onEnd={handleEnd}
        onProgress={onProgress}
        className="vimeo-player"
      />
    </div>
  );
};

// Video List Item Component
const VideoListItem = ({ video, isCurrent, isWatched, onClick, index }) => {
  const formatDuration = (duration) => {
    if (!duration) return 'N/A';
    if (typeof duration === 'number') {
      const minutes = Math.floor(duration / 60);
      const seconds = duration % 60;
      return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    }
    return duration;
  };

  return (
    <div 
      className={`video-list-item ${isCurrent ? 'active' : ''} ${isWatched ? 'watched' : ''}`}
      onClick={onClick}
    >
      <div className="video-item-indicator">
        {isWatched ? (
          <div className="watched-indicator">
            <i className="fas fa-check"></i>
          </div>
        ) : (
          <div className="video-number">{index + 1}</div>
        )}
      </div>
      
      <div className="video-item-content">
        <div className="video-item-header">
          <h6 className="video-title">{video.video_title || 'Untitled Video'}</h6>
          {isCurrent && (
            <Badge className="current-badge" style={{ backgroundColor: PRIMARY_COLOR }}>
              <i className="fas fa-play me-1"></i>
              Playing
            </Badge>
          )}
        </div>
        
        <div className="video-meta">
          <span className="video-duration">
            <i className="fas fa-clock me-1"></i>
            {formatDuration(video.duration)}
          </span>
          
          {!video.available && (
            <Badge bg="secondary" className="ms-2">
              <i className="fas fa-lock me-1"></i>
              Locked
            </Badge>
          )}
          
          {video.subscription_required && (
            <Badge className="premium-badge">
              <i className="fas fa-crown me-1"></i>
              Premium
            </Badge>
          )}
        </div>
      </div>
    </div>
  );
};

// Progress Ring Component
const ProgressRing = ({ progress, size = 40, strokeWidth = 4 }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const strokeDashoffset = circumference - (progress / 100) * circumference;

  return (
    <div className="progress-ring" style={{ width: size, height: size }}>
      <svg width={size} height={size}>
        <circle
          className="progress-ring-bg"
          stroke="#e6f0ff"
          strokeWidth={strokeWidth}
          fill="transparent"
          r={radius}
          cx={size / 2}
          cy={size / 2}
        />
        <circle
          className="progress-ring-fill"
          stroke={PRIMARY_COLOR}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          fill="transparent"
          r={radius}
          cx={size / 2}
          cy={size / 2}
          style={{
            strokeDasharray: circumference,
            strokeDashoffset,
            transform: 'rotate(-90deg)',
            transformOrigin: '50% 50%'
          }}
        />
        <text
          x="50%"
          y="50%"
          textAnchor="middle"
          dy=".3em"
          className="progress-text"
          fill={PRIMARY_COLOR}
          fontSize="12"
          fontWeight="bold"
        >
          {Math.round(progress)}%
        </text>
      </svg>
    </div>
  );
};

// Main WatchVideo component
const WatchVideoContent = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const { makeRequest, loading: apiLoading } = useApi();
  const [videoData, setVideoData] = useState(null);
  const [error, setError] = useState(null);
  const [enrolling, setEnrolling] = useState(false);
  const [videoProgress, setVideoProgress] = useState(0);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [watchedVideos, setWatchedVideos] = useState(new Set());
  const [savingProgress, setSavingProgress] = useState(false);
  const [hasSubscription, setHasSubscription] = useState(false);

  // Check subscription status
  const checkSubscriptionStatus = useCallback(() => {
    const subscriptionStatus = localStorage.getItem("codefussion-subscription-status");
    console.log("Subscription status:", subscriptionStatus);
    
    // Check if subscription exists and is active
    if (subscriptionStatus === "active" || subscriptionStatus === "Active" || subscriptionStatus === "ACTIVE") {
      setHasSubscription(true);
      return true;
    } else {
      setHasSubscription(false);
      // Show subscription required message
      Swal.fire({
        title: "Subscription Required",
        text: "You need an active subscription to watch videos. Please subscribe to continue.",
        icon: "warning",
        confirmButtonText: "Go to Plans",
        cancelButtonText: "Maybe Later",
        showCancelButton: true,
        backdrop: `rgba(143, 70, 0, 0.3) left top no-repeat`,
        confirmButtonColor: PRIMARY_COLOR
      }).then((result) => {
        if (result.isConfirmed) {
          window.location.href = "/plans";
        } else {
          // If they cancel, redirect to videos page
          setTimeout(() => {
            navigate('/videos');
          }, 1000);
        }
      });
      return false;
    }
  }, [navigate]);

  // Get authentication tokens
  const getToken = useCallback(() => localStorage.getItem('codefussion-login-token'), []);
  const getEmail = useCallback(() => localStorage.getItem('codefussion-student-email'), []);

  // Enhanced error handler
  const handleApiError = useCallback((error) => {
    console.error('API Error:', error);
    
    if (error.code === 'NETWORK_ERROR' || !isOnline) {
      setError('Network error. Please check your internet connection.');
      return;
    }
    
    if (error.response?.status === 401) {
      setError('Authentication required. Redirecting to login...');
      setTimeout(() => navigate('/login', { replace: true }), 2000);
      return;
    }
    
    if (error.response?.status === 404) {
      setError('Video not found. It may have been removed or you may not have access.');
      return;
    }
    
    if (error.response?.status >= 500) {
      setError('Server error. Please try again later.');
      return;
    }
    
    setError('Failed to load video data. Please try again.');
  }, [isOnline, navigate]);

  // Update course progress
  const updateCourseProgress = useCallback(async (completedLessons) => {
    if (!videoData || !videoData.length) return;

    try {
      setSavingProgress(true);
      const email = getEmail();
      const currentVideo = videoData[currentVideoIndex];
      
      const progressData = {
        email: email,
        course_id: currentVideo.video_id || id,
        title: currentVideo.course_title || 'Untitled Course',
        progress: completedLessons / videoData.length,
        image: currentVideo.image || '',
        completedLessons: completedLessons,
        totalLessons: videoData.length,
        lastAccessed: new Date().toISOString(),
        category: 'video' // You can adjust this based on your categories
      };

      console.log('Updating progress:', progressData);

      const response = await makeRequest({
        method: 'POST',
        url: API_ENDPOINTS.UPDATE_PROGRESS,
        data: progressData,
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
      });

      if (response.status === 'success') {
        console.log('Progress updated successfully:', response.data);
      } else {
        console.warn('Progress update warning:', response.message);
      }
    } catch (err) {
      console.error('Failed to update progress:', err);
      // Don't show error to user for progress updates
    } finally {
      setSavingProgress(false);
    }
  }, [videoData, currentVideoIndex, id, getEmail, makeRequest]);

  // Mark video as watched
  const markVideoAsWatched = useCallback((videoIndex) => {
    if (!videoData || videoIndex >= videoData.length) return;

    const videoId = videoData[videoIndex].id;
    setWatchedVideos(prev => {
      const newWatched = new Set(prev);
      newWatched.add(videoId);
      
      // Update course progress
      updateCourseProgress(newWatched.size);
      
      return newWatched;
    });
  }, [videoData, updateCourseProgress]);

  // Network status monitoring
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Redirect if not authenticated and check subscription
  useEffect(() => {
    const token = getToken();
    if (!token) {
      navigate('/login', { replace: true });
    } else {
      // Check subscription status only if authenticated
      checkSubscriptionStatus();
    }
  }, [navigate, getToken, checkSubscriptionStatus]);

  // Fetch video data only if user has subscription
  const fetchVideoData = useCallback(async (signal) => {
    // Don't fetch videos if user doesn't have subscription
    if (!hasSubscription) {
      return;
    }

    if (!id) {
      setError('Invalid video ID');
      return;
    }

    try {
      const token = getToken();
      if (!token) {
        setError('Authentication required');
        return;
      }

      const response = await makeRequest({
        method: 'POST',
        url: API_ENDPOINTS.GET_VIDEO,
        data: { id },
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: token,
        },
        signal
      });

      console.log('Video API Response:', response);

      if (response && Array.isArray(response) && response.length > 0) {
        setVideoData(response);
        setError(null);
        setCurrentVideoIndex(0);
      } else if (response && response.status === 0) {
        throw new Error('Failed to fetch video data from server');
      } else if (response && response.data) {
        setVideoData(response.data);
        setError(null);
        setCurrentVideoIndex(0);
      } else {
        setVideoData([]);
        setError('No video data found');
      }

    } catch (err) {
      if (err.name !== 'AbortError') {
        handleApiError(err);
        
        Swal.fire({
          title: 'Error!',
          text: 'An error occurred while loading the video. Please try again.',
          icon: 'error',
          confirmButtonText: 'OK',
        });
      }
    }
  }, [id, makeRequest, getToken, handleApiError, hasSubscription]);

  useEffect(() => {
    const token = getToken();
    if (token && id && hasSubscription) {
      const abortController = new AbortController();
      fetchVideoData(abortController.signal);
      
      return () => abortController.abort();
    }
  }, [id, fetchVideoData, getToken, hasSubscription]);

  // Get current video
  const currentVideo = useMemo(() => {
    return videoData?.[currentVideoIndex] || null;
  }, [videoData, currentVideoIndex]);

  // Memoized video information for performance
  const videoInfo = useMemo(() => {
    if (!currentVideo) return null;
    
    return {
      title: currentVideo.course_title || 'Untitled Course',
      videoTitle: currentVideo.video_title || 'Untitled Video',
      description: currentVideo.video_description || 'No description available.',
      cleanTitle: (currentVideo.course_title || '').trim().toLowerCase()
    };
  }, [currentVideo]);

  // Handle video selection from the list
  const handleVideoSelect = useCallback((index) => {
    setCurrentVideoIndex(index);
    setVideoProgress(0);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // Handle next video
  const handleNextVideo = useCallback(() => {
    if (!videoData || currentVideoIndex >= videoData.length - 1) {
      // If it's the last video, show completion message
      Swal.fire({
        title: '🎉 Course Completed!',
        text: 'Congratulations! You have completed all videos in this course.',
        icon: 'success',
        confirmButtonText: 'View Courses',
        background: '#f8f9fa',
        confirmButtonColor: PRIMARY_COLOR
      }).then(() => {
        navigate('/courses');
      });
      return;
    }

    // Mark current video as watched
    markVideoAsWatched(currentVideoIndex);
    
    // Move to next video
    setCurrentVideoIndex(prev => prev + 1);
    setVideoProgress(0);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [videoData, currentVideoIndex, navigate, markVideoAsWatched]);

  // Handle video end
  const handleVideoEnd = useCallback(() => {
    console.log('Video ended, marking as watched');
    markVideoAsWatched(currentVideoIndex);
    
    // Auto-advance to next video after 3 seconds
    setTimeout(() => {
      if (currentVideoIndex < videoData.length - 1) {
        Swal.fire({
          title: 'Great Job! 🎯',
          text: 'Ready for the next video?',
          icon: 'success',
          showCancelButton: true,
          confirmButtonText: 'Next Video',
          cancelButtonText: 'Watch Again',
          confirmButtonColor: PRIMARY_COLOR,
          background: '#f8f9fa'
        }).then((result) => {
          if (result.isConfirmed) {
            handleNextVideo();
          }
        });
      }
    }, 2000);
  }, [currentVideoIndex, videoData, handleNextVideo, markVideoAsWatched]);

  const handleBackToVideos = () => {
    navigate('/videos');
  };

  const handleRetry = () => {
    setError(null);
    const abortController = new AbortController();
    fetchVideoData(abortController.signal);
  };

  const handleVideoProgress = useCallback((data) => {
    setVideoProgress(data.percent);
  }, []);

  const handleVideoError = useCallback((error) => {
    console.error('Video player error:', error);
    Swal.fire({
      title: 'Video Error',
      text: 'There was a problem loading the video. Please try again.',
      icon: 'error',
      confirmButtonText: 'OK',
      confirmButtonColor: PRIMARY_COLOR
    });
  }, []);

  // Calculate course stats
  const courseStats = useMemo(() => {
    if (!videoData) return null;
    
    const totalVideos = videoData.length;
    const watchedVideosCount = watchedVideos.size;
    const progressPercentage = totalVideos > 0 ? (watchedVideosCount / totalVideos) * 100 : 0;
    
    const totalDuration = videoData.reduce((total, video) => {
      if (video.duration && typeof video.duration === 'number') {
        return total + video.duration;
      }
      return total;
    }, 0);
    
    const hours = Math.floor(totalDuration / 3600);
    const minutes = Math.floor((totalDuration % 3600) / 60);
    
    return {
      totalVideos,
      watchedVideosCount,
      progressPercentage,
      totalDuration: { hours, minutes },
      premiumVideos: videoData.filter(video => video.subscription_required).length,
      availableVideos: videoData.filter(video => video.available).length
    };
  }, [videoData, watchedVideos]);

  // Show subscription required message if no subscription
  if (!hasSubscription) {
    return (
      <div className="watch-video-page">
        <Topbar />
        <Container fluid className="watch-video-container">
          <Row>
            <Col lg={12}>
              <div className="subscription-required-message text-center py-5">
                <div className="subscription-icon mb-4">
                  <i className="fas fa-crown fa-4x text-warning"></i>
                </div>
                <h2 className="mb-3">Premium Content Access Required</h2>
                <p className="lead mb-4">
                  You need an active subscription to watch this video content.
                </p>
                <p className="text-muted mb-4">
                  Upgrade your account to unlock our premium video library and continue learning.
                </p>
                <div className="subscription-actions">
                  <button 
                    className="btn btn-primary btn-lg me-3"
                    onClick={() => navigate('/plans')}
                  >
                    <i className="fas fa-gem me-2"></i>
                    View Subscription Plans
                  </button>
                  <button 
                    className="btn btn-outline-secondary btn-lg"
                    onClick={() => navigate('/videos')}
                  >
                    <i className="fas fa-arrow-left me-2"></i>
                    Back to Videos
                  </button>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  // Show full screen loading state only when initially loading
  if (apiLoading && !videoData && !error) {
    return <FullScreenLoader />;
  }

  return (
    <div className="watch-video-page">
      <Topbar />
      
      <Container fluid className="watch-video-container">
        <Row>
          {/* Main Video Content */}
          <Col lg={8} className="video-content">
            <div className="video-header">
              <button 
                className="btn-back"
                onClick={handleBackToVideos}
                aria-label="Back to videos page"
              >
                <i className="fas fa-arrow-left me-2"></i>
                Back to Videos
              </button>
              
              <div className="header-actions">
                {savingProgress && (
                  <div className="saving-indicator">
                    <Spinner animation="border" size="sm" className="me-2" />
                    Saving progress...
                  </div>
                )}
                
                {!isOnline && (
                  <div className="offline-indicator">
                    <i className="fas fa-wifi me-1"></i>
                    Offline
                  </div>
                )}
              </div>
            </div>

            {error ? (
              <div className="error-state">
                <div className="error-icon">
                  <i className="fas fa-exclamation-circle"></i>
                </div>
                <h3>Oops! Something went wrong</h3>
                <p>{error}</p>
                <div className="error-actions">
                  <button 
                    className="btn-primary"
                    onClick={handleRetry}
                    disabled={!isOnline}
                  >
                    <i className="fas fa-redo me-2"></i>
                    Try Again
                  </button>
                  <button 
                    className="btn-secondary"
                    onClick={handleBackToVideos}
                  >
                    Back to Videos
                  </button>
                </div>
              </div>
            ) : currentVideo ? (
              <Card className="video-card">
                <Card.Body>
                  <VideoPlayer 
                    video={currentVideo} 
                    onProgress={handleVideoProgress}
                    onEnd={handleVideoEnd}
                    onError={handleVideoError}
                  />
                  
                  <div className="video-info">
                    <div className="video-header-info">
                      <div className="video-badge">
                        Video {currentVideoIndex + 1} of {videoData?.length || 0}
                      </div>
                      <ProgressRing progress={courseStats?.progressPercentage || 0} />
                    </div>
                    
                    <h1 className="video-main-title">
                      {videoInfo?.videoTitle}
                    </h1>
                    
                    {/* Progress indicator */}
                    {videoProgress > 0 && (
                      <div className="video-progress-indicator">
                        <div className="progress-label">
                          <span>Watching: {Math.round(videoProgress * 100)}%</span>
                        </div>
                        <div className="progress-bar-container">
                          <div 
                            className="progress-bar-fill"
                            style={{ width: `${videoProgress * 100}%`, backgroundColor: PRIMARY_COLOR }}
                          ></div>
                        </div>
                      </div>
                    )}
                    
                    <div className="video-description">
                      {videoInfo?.description}
                    </div>

                    {/* Next Video CTA */}
                    <div className="next-video-cta">
                      <div className="cta-content">
                        <h5>Ready for the next challenge? 🚀</h5>
                        <p>Continue your learning journey with the next video</p>
                        <button
                          className="btn-next"
                          onClick={handleNextVideo}
                          disabled={!currentVideo.available || !isOnline}
                        >
                          {currentVideoIndex >= videoData.length - 1 ? (
                            <>
                              <i className="fas fa-trophy me-2"></i>
                              Complete Course
                            </>
                          ) : (
                            <>
                              <i className="fas fa-arrow-right me-2"></i>
                              Next Video
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            ) : (
              <div className="empty-state">
                <div className="empty-icon">
                  <i className="fas fa-film"></i>
                </div>
                <h3>No Video Found</h3>
                <p>This video might not exist or you may not have access to it.</p>
                <button className="btn-primary" onClick={handleBackToVideos}>
                  Browse Videos
                </button>
              </div>
            )}
          </Col>

          {/* Sidebar for course videos and actions */}
          <Col lg={4} className="sidebar-content">
            {/* Course Progress */}
            {courseStats && (
              <Card className="course-progress-card">
                <Card.Header>
                  <h5>
                    <i className="fas fa-trophy me-2"></i>
                    Your Progress
                  </h5>
                </Card.Header>
                <Card.Body>
                  <div className="progress-stats">
                    <div className="progress-main">
                      <ProgressRing progress={courseStats.progressPercentage} size={80} />
                      <div className="progress-text">
                        <h4>{Math.round(courseStats.progressPercentage)}% Complete</h4>
                        <p>{courseStats.watchedVideosCount} of {courseStats.totalVideos} videos watched</p>
                      </div>
                    </div>
                    
                    <div className="progress-details">
                      <div className="progress-item">
                        <i className="fas fa-play-circle" style={{ color: PRIMARY_COLOR }}></i>
                        <span>Total Videos</span>
                        <Badge bg="light" text="dark">{courseStats.totalVideos}</Badge>
                      </div>
                      <div className="progress-item">
                        <i className="fas fa-check-circle" style={{ color: '#28a745' }}></i>
                        <span>Watched</span>
                        <Badge bg="success">{courseStats.watchedVideosCount}</Badge>
                      </div>
                      <div className="progress-item">
                        <i className="fas fa-crown" style={{ color: '#ffc107' }}></i>
                        <span>Premium</span>
                        <Badge bg="warning" text="dark">{courseStats.premiumVideos}</Badge>
                      </div>
                      <div className="progress-item">
                        <i className="fas fa-clock" style={{ color: PRIMARY_COLOR }}></i>
                        <span>Duration</span>
                        <Badge bg="light" text="dark">
                          {courseStats.totalDuration.hours > 0 
                            ? `${courseStats.totalDuration.hours}h ${courseStats.totalDuration.minutes}m`
                            : `${courseStats.totalDuration.minutes}m`
                          }
                        </Badge>
                      </div>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            )}

            {/* Video Playlist */}
            {videoData && videoData.length > 0 && (
              <Card className="playlist-card">
                <Card.Header>
                  <h5>
                    <i className="fas fa-list-ol me-2"></i>
                    Course Playlist
                    <Badge bg="primary" className="ms-2">{videoData.length}</Badge>
                  </h5>
                </Card.Header>
                <Card.Body className="playlist-body">
                  <div className="video-playlist">
                    {videoData.map((video, index) => (
                      <VideoListItem
                        key={video.id}
                        video={video}
                        isCurrent={index === currentVideoIndex}
                        isWatched={watchedVideos.has(video.id)}
                        onClick={() => handleVideoSelect(index)}
                        index={index}
                      />
                    ))}
                  </div>
                </Card.Body>
              </Card>
            )}

            {/* Quick Actions */}
            <Card className="actions-card">
              <Card.Header>
                <h5>
                  <i className="fas fa-bolt me-2"></i>
                  Quick Actions
                </h5>
              </Card.Header>
              <Card.Body>
                <div className="quick-actions">
                  <button 
                    className="btn-action-primary"
                    onClick={handleNextVideo}
                    disabled={!currentVideo?.available || !isOnline}
                  >
                    <i className="fas fa-arrow-right me-2"></i>
                    {currentVideoIndex >= (videoData?.length - 1) ? 'Complete Course' : 'Next Video'}
                  </button>
                  
                  <button 
                    className="btn-action-secondary"
                    onClick={() => navigate('/courses')}
                  >
                    <i className="fas fa-graduation-cap me-2"></i>
                    Browse Courses
                  </button>
                  
                  {error && (
                    <button 
                      className="btn-action-outline"
                      onClick={handleRetry}
                      disabled={!isOnline}
                    >
                      <i className="fas fa-redo me-2"></i>
                      Reload Content
                    </button>
                  )}
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

// Main component that wraps with ProSidebarProvider
const WatchVideo = () => {
  return (
    <MyProSidebarProvider>
      <WatchVideoContent />
    </MyProSidebarProvider>
  );
};

export default WatchVideo;